// to aid the use of the hooks
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useCallback, useContext, useEffect } from 'react';
import { ApolloContext } from 'react-apollo';
import {
  MutationOptions as ApolloMutationOptions,
  QueryOptions as ApolloQueryOptions,
  OperationVariables,
} from 'apollo-client';
// eslint-disable-next-line import/no-extraneous-dependencies
import { FetchResult } from 'apollo-link';

export type QueryOptions<T> = Partial<
  // eslint-disable-next-line  @typescript-eslint/member-delimiter-style
  ApolloQueryOptions<T> & { skip?: boolean }
>;

type InitialMutationOptions<T> = Partial<ApolloMutationOptions<T>>;

export type DocumentNode<T> = ApolloQueryOptions<T>['query'];
export type MutDocumentNode<T, V> = ApolloMutationOptions<T, V>['mutation'];
export type MutationOptions<T, TVariables> = Pick<ApolloMutationOptions<T, TVariables>, 'variables'>;
export type MutationFn<T = any, TVariables = OperationVariables> = (
  options: MutationOptions<T, TVariables>,
) => Promise<FetchResult<T>>;
export type QueryHookReturn<T> = { data: T | null; loading: boolean; error: Error | null; refetch: Function };

/**
 * A custom React Hook for fetching GraphQL data using Apollo Client,
 * mimicking the behavior of Apollo's useQuery.
 * It handles loading, error, and data states, and provides a refetch mechanism.
 *
 * @param {DocumentNode<Vars>} query - The GraphQL query document (e.g., gql`query { ... }`).
 * @param {QueryOptions<Vars>} options - Optional Apollo Client query options (e.g., variables, fetchPolicy, skip).
 * @returns {{data: any, loading: boolean, error: Error | null, refetch: Function}}
 * An object containing the fetched data, loading state, error object, and a refetch function.
 */
export const useQuery = <T, Vars = {}>(
  query: DocumentNode<Vars>,
  options: QueryOptions<Vars> = {},
): QueryHookReturn<T> => {
  const apolloCtx = useContext(ApolloContext);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const client = apolloCtx && apolloCtx.client;

  const executeQuery = useCallback(async () => {
    if (options.skip) {
      setLoading(false);
      setData(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (!client) {
        return;
      }

      const { data: resultData, errors } =
        (await client.query({
          query,
          ...options,
        })) || {};

      if (errors && errors.length > 0) {
        setError(new Error(errors.map((e: Error) => e.message).join(', ')));
        setData(null);
      } else {
        setData(resultData);
      }
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client, query, options, refetchTrigger]);

  useEffect(() => {
    executeQuery();
  }, [options.skip, refetchTrigger]);

  const refetch = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1);
  }, []);

  return { data, loading, error, refetch };
};

/**
 * A conceptual custom React Hook for performing GraphQL mutations using Apollo Client.
 * It returns a function to trigger the mutation and the state of the mutation.
 *
 * @param {object} mutation - The GraphQL mutation document (e.g., gql`mutation { ... }`).
 * @param {object} initialOptions - Optional initial Apollo Client mutation options.
 * @returns {[Function, { data: any, loading: boolean, error: Error | null }]}
 * A tuple containing the mutate function and an object with data, loading, and error states.
 */
export const useMutation = <Vars = Record<string, any>, T = any>(
  mutation: MutDocumentNode<T, Vars>,
  initialOptions: InitialMutationOptions<T> = {},
): [MutationFn<T, Vars>, { data: T | null; loading: boolean; error: Error | null }] => {
  const apolloCtx = useContext(ApolloContext);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const client = apolloCtx && apolloCtx.client;

  const mutateFunction = useCallback(
    async (mutationOptions: MutationOptions<T, Vars> = {}) => {
      setLoading(true);
      setError(null);
      try {
        if (!client) {
          return { data: null, errors: [new Error('Client is falsey!')] };
        }

        // Merge initial options with options provided during the mutateFunction call
        const mergedOptions = {
          ...(initialOptions || {}),
          ...(mutationOptions || {}),
          variables: {
            ...((initialOptions && initialOptions.variables) || {}),
            ...((mutationOptions && mutationOptions.variables) || {}),
          },
        };

        const { data: resultData, errors } =
          (await client.mutate({
            mutation,
            ...mergedOptions,
          })) || {};

        if (errors && errors.length > 0) {
          setError(new Error(errors.map((e: Error) => e.message).join(', ')));
          setData(null);
        } else {
          setData(resultData || null);
        }
        return { data: resultData, errors }; // Return the result, similar to Apollo's mutate function
      } catch (err) {
        setError(err as Error);
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
      // the param `initialOptions` is omited as a dependency,
      // it might not be cached and that would cause the function to be re-rendered
    },
    [client, mutation],
  ) as MutationFn<T, Vars>;

  return [mutateFunction, { data, loading, error }];
};
